import { useState } from 'react'
import { Link } from 'react-router-dom'
import Avatar from './Avatar.jsx'
import { ApiError, api } from '../lib/api.js'

/**
 * Comments on a review or a list. Plain text, no threading, no editing.
 *
 * All three of those are the alpha's scope rather than an oversight: PLAN §25
 * REV-001 asks for comments, and a reply tree is a different feature with its
 * own collapse rules, moderation surface and notification semantics.
 */
export default function CommentThread({ kind, publicId, comments, onChange, canPost = true }) {
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  // Which comment's delete is in flight. One id rather than a map: a person
  // has one pointer, and the row's own button is what gets disabled — the
  // rest of the thread stays usable. Separate from `busy`, which is the
  // form's.
  const [removing, setRemoving] = useState(null)

  async function post(event) {
    event.preventDefault()
    const body = draft.trim()
    if (!body) return
    setBusy(true)
    setError('')
    try {
      await api.addComment(kind, publicId, { body })
      setDraft('')
      await onChange?.()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not post.')
    } finally {
      setBusy(false)
    }
  }

  async function remove(comment) {
    // The second press of a delete that is already running would 404 on a
    // comment the first press removed, and print a failure about a delete
    // that landed. The guard is the state, not only the `disabled` below:
    // the attribute is what a person sees, this is what makes it true.
    if (removing) return
    setRemoving(comment.public_id)
    setError('')
    try {
      await api.deleteComment(comment.public_id)
      await onChange?.()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not delete.')
    } finally {
      setRemoving(null)
    }
  }

  return (
    <section className="comment-thread">
      {comments?.length ? (
        <ul className="comment-list">
          {comments.map((comment) => (
            <li key={comment.public_id} className="comment">
              <Link to={`/profile/${comment.author?.username}`} className="comment-avatar">
                <Avatar
                  name={comment.author?.display_name || comment.author?.username}
                  avatarUrl={comment.author?.avatar_url}
                  size={28}
                />
              </Link>
              <div className="row-main">
                <p className="comment-meta mb-0">
                  <Link to={`/profile/${comment.author?.username}`}>
                    {comment.author?.display_name || comment.author?.username}
                  </Link>
                  {comment.is_mine ? <span className="footnote"> · you</span> : null}
                </p>
                {/* Plain text, so it goes in as a text node. A comment is not
                    Markdown and does not go near `dangerouslySetInnerHTML`. */}
                <p className="comment-body mb-0">{comment.body}</p>
              </div>
              {comment.is_mine ? (
                <button
                  type="button"
                  className="btn btn-quiet btn-sm"
                  onClick={() => remove(comment)}
                  disabled={removing === comment.public_id}
                >
                  Delete
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="footnote mb-0">No comments yet.</p>
      )}

      {canPost ? (
        <form className="comment-form" onSubmit={post}>
          <label className="sr-only" htmlFor={`comment-${publicId}`}>
            Add a comment
          </label>
          <input
            id={`comment-${publicId}`}
            className="input"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="Say something"
            maxLength={2000}
          />
          <button
            type="submit"
            className="btn btn-tonal btn-sm"
            disabled={busy || !draft.trim()}
          >
            Post
          </button>
        </form>
      ) : null}

      {error ? (
        <p className="footnote error mb-0" role="alert">
          {error}
        </p>
      ) : null}
    </section>
  )
}
